import React from 'react';

import { Box, Tooltip } from '@mantine/core';
import {
  IconEdit,
  IconEye,
  IconEyeOff,
  IconPresentation,
  IconRefresh,
  IconTrash,
} from '@tabler/icons-react';
import Menu, { MenuItemProps } from 'components/Menu';

interface WorkoutAssignmentMenuProps {
  canMutate: boolean;
  hasWorkoutId: boolean;
  owner: 'athlete' | 'classType';
  isVisibleToAthletes: boolean;
  isLocked?: boolean;
  onToggleVisibility: () => void;
  onViewWhiteboard?: () => void;
  onEditWorkout: () => void;
  onChangeWorkout: () => void;
  onDeleteWorkout: () => void;
  target: JSX.Element;
}

const visibilityTitle = (owner: 'athlete' | 'classType', isVisibleToAthletes: boolean): string => {
  if (owner === 'athlete') return isVisibleToAthletes ? 'Hide from Athlete' : 'Show to Athlete';

  return isVisibleToAthletes ? 'Hide from Athletes' : 'Show to Athletes';
};

export const lockedHint = (owner: 'athlete' | 'classType'): string => {
  if (owner === 'athlete')
    return 'This athlete has logged results, so this workout can no longer be changed.';

  return 'Athletes have logged results, so this workout can no longer be changed.';
};

const WorkoutAssignmentMenu = ({
  canMutate,
  hasWorkoutId,
  owner,
  isVisibleToAthletes,
  isLocked = false,
  onToggleVisibility,
  onViewWhiteboard,
  onEditWorkout,
  onChangeWorkout,
  onDeleteWorkout,
  target,
}: WorkoutAssignmentMenuProps): JSX.Element => {
  const items: MenuItemProps[] = [
    {
      title: visibilityTitle(owner, isVisibleToAthletes),
      icon: isVisibleToAthletes ? <IconEyeOff size={14} /> : <IconEye size={14} />,
      onClick: onToggleVisibility,
      disabled: isLocked,
      invisible: !canMutate || !hasWorkoutId,
      'data-cy': 'workoutMenuVisibility',
    },
  ];

  if (onViewWhiteboard) {
    items.push({
      title: 'View Whiteboard',
      icon: <IconPresentation size={14} />,
      onClick: onViewWhiteboard,
      'data-cy': 'workoutMenuWhiteboard',
    });
  }

  items.push(
    {
      title: 'Edit Workout',
      icon: <IconEdit size={14} />,
      onClick: onEditWorkout,
      disabled: isLocked,
      invisible: !canMutate,
      'data-cy': 'workoutMenuEdit',
    },
    {
      title: 'Change Workout',
      icon: <IconRefresh size={14} />,
      onClick: onChangeWorkout,
      disabled: isLocked,
      invisible: !canMutate,
      'data-cy': 'workoutMenuChange',
    },
    {
      title: 'Delete',
      icon: <IconTrash size={14} />,
      color: 'red',
      onClick: onDeleteWorkout,
      divider: 'top',
      disabled: isLocked,
      invisible: !canMutate || !hasWorkoutId,
      'data-cy': 'workoutMenuDelete',
    },
  );

  const menu = <Menu position='bottom-end' target={target} items={items} />;

  if (!isLocked) return menu;

  return (
    <Tooltip label={lockedHint(owner)} withArrow withinPortal position='left'>
      <Box component='span' sx={{ display: 'inline-flex' }}>
        {menu}
      </Box>
    </Tooltip>
  );
};

export default WorkoutAssignmentMenu;
